import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IGoogleAdsAdText } from '@feathr/blackbox';
import { Button, GoogleAdPreview } from '@feathr/components';
import { pickRandomItems } from '@feathr/hooks';

import type { IAd, ISitelink } from '../AdStep.types';
import { toDisplayUrl, toSitelinkLabels } from '../AdStep.utils';

/**
 * How many of an ad's assets Google assembles into one result. An ad may carry up to 15
 * headlines and 4 descriptions; it never shows them all at once.
 *
 * @see https://support.google.com/google-ads/answer/7684791
 */
const HEADLINES_SHOWN = 3;
const DESCRIPTIONS_SHOWN = 2;

/** The picked combination, tagged with what was available when it was picked. */
interface IShownKeys {
  headlineSignature: string;
  descriptionSignature: string;
  headlines: string[];
  descriptions: string[];
}

interface IAdPreviewProps {
  readonly ad: IAd;
  /** The ad group's sitelinks — every ad in the group shows them. */
  readonly sitelinks: ISitelink[];
  /** The advertiser name Google shows above the display URL. */
  readonly businessName?: string;
  readonly name: string;
}

function getFilled(items: readonly IGoogleAdsAdText[]): IGoogleAdsAdText[] {
  return items.filter((item) => !!item.text.trim());
}

/** The keys of a random combination, as a stable signature of what is available to pick. */
function pickKeys(items: readonly IGoogleAdsAdText[], count: number): string[] {
  return pickRandomItems(
    items.map((item) => item.key),
    count,
  );
}

/**
 * One ad as Google would assemble it: a random combination of its headlines and
 * descriptions rather than all of them at once, the same way a Smart campaign's
 * `AdPreviewCard` shows its single ad.
 *
 * Which rows are shown is held in state and their text read live, so typing into a line
 * the preview is already showing updates it, while the combination stays put. It is
 * re-picked when a row is added, removed or emptied — the ad's shape has changed — and on
 * demand, since one combination says little about how the ad will actually appear.
 *
 * @param props - {@link IAdPreviewProps}.
 */
function AdPreview({ ad, sitelinks, businessName, name }: Readonly<IAdPreviewProps>): JSX.Element {
  const { t } = useTranslation();

  const filledHeadlines = getFilled(ad.headlines);
  const filledDescriptions = getFilled(ad.descriptions);
  const headlineSignature = filledHeadlines.map((headline) => headline.key).join('|');
  const descriptionSignature = filledDescriptions.map((description) => description.key).join('|');

  function pick(): IShownKeys {
    return {
      headlineSignature,
      descriptionSignature,
      headlines: pickKeys(filledHeadlines, HEADLINES_SHOWN),
      descriptions: pickKeys(filledDescriptions, DESCRIPTIONS_SHOWN),
    };
  }

  const [shownKeys, setShownKeys] = useState<IShownKeys>(pick);

  /*
   * Re-picked during render rather than in an effect, so the preview never paints a
   * combination built from rows that have since gone. The signatures are what is compared,
   * not the arrays: those are rebuilt every render, so comparing them would re-pick on
   * every keystroke and shuffle the preview as the user types.
   */
  if (
    shownKeys.headlineSignature !== headlineSignature ||
    shownKeys.descriptionSignature !== descriptionSignature
  ) {
    setShownKeys(pick());
  }

  function handleRegenerate(): void {
    setShownKeys(pick());
  }

  /*
   * Read in the ad's own order, not the order they were picked in — Google composes the
   * headlines as written, and a shuffled reading order would misrepresent the result.
   */
  const headlines = filledHeadlines
    .filter((headline) => shownKeys.headlines.includes(headline.key))
    .map((headline) => headline.text.trim());
  const descriptions = filledDescriptions
    .filter((description) => shownKeys.descriptions.includes(description.key))
    .map((description) => description.text.trim());

  return (
    <>
      <GoogleAdPreview
        businessName={businessName}
        descriptions={descriptions}
        headlines={headlines}
        name={name}
        sitelinks={toSitelinkLabels(sitelinks)}
        t={t}
        websiteUrl={toDisplayUrl(ad.finalUrl)}
      />
      {/* Hidden until there is a second combination to show, where it would do nothing. */}
      {(filledHeadlines.length > HEADLINES_SHOWN ||
        filledDescriptions.length > DESCRIPTIONS_SHOWN) && (
        <Flex justify={'flex-end'}>
          <Button
            name={`${name}-regenerate`}
            onClick={handleRegenerate}
            prefix={<FontAwesomeIcon icon={faArrowsRotate} />}
            type={'naked'}
          >
            {t('Show another combination')}
          </Button>
        </Flex>
      )}
    </>
  );
}

export default AdPreview;
